import AxiosInstance from "./AxiosInstance";
import {constructFilter, constructParameters} from "../utils/AdvancedFilterApiUtils.js";
import {getContentDispositionFilename} from "../utils/HeaderUtils";

const ongoingRequests = {};

// Helper function to strip query parameters
const stripQueryParams = (url) => {
    return url.split('?')[0];
};

const ApiClient = {
    get(url, params, page, filter, sort) {
        const baseUrl = url;
        const parameters = constructParameters(page, filter, sort);
        if (parameters && parameters.length > 0) {
            url += "?" + parameters;
        }

        // Cancel previous request if it exists
        if (ongoingRequests[baseUrl]) {
            ongoingRequests[baseUrl].abort();
        }
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[baseUrl] = controller;
        ongoingRequests[url] = controller;


        let options = {
            method: 'GET',
            url: url,
            params: {
                ...params
            },
            signal: controller.signal // Include the signal in the options
        };

        return this.callAxios(options, url);
    },

    getBinary(url, params, page, filter, sort) {
        const parameters = constructParameters(page, filter, sort);
        parameters.length > 0 && (url += "?" + parameters);

        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        let options = {
            responseType: 'blob',
            method: 'GET',
            url: url,
            params: {
                ...params
            },
            signal: controller.signal // Include the signal in the options
        };

        return this.callAxios(options, url);
    },

    post(url, data) {
        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        const options = {
            method: 'POST',
            url: url,
            data: data,
            signal: controller.signal // Include the signal in the options
        };
        return this.callAxios(options, url);
    },

    put(url, data) {
        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        const options = {
            method: 'PUT',
            url: url,
            data: data,
            signal: controller.signal // Include the signal in the options
        };
        return this.callAxios(options, url);
    },

    delete(url) {
        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        const options = {
            method: 'DELETE',
            url: url,
            signal: controller.signal // Include the signal in the options
        };
        return this.callAxios(options, url);
    },

    request(method, url, data, params, responseType) {
        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        let options = {
            method: method,
            url: url,
            data: data,
            responseType: responseType,
            params: {
                ...params
            },
            signal: controller.signal // Include the signal in the options
        };

        console.info("ApiClient.request() called. ", JSON.stringify(options));
        return this.callAxios(options, url);
    },

    async getCSVFile(url, headers = {}) {
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;
        const options = {
            method: 'GET',
            url: url,
            responseType: 'text',
            headers: {
                ...headers,
                'Accept': 'text/csv',
                'Content-Type': 'text/csv'
            },
            signal: controller.signal // Include the signal in the options
        };

        return this.callAxios(options, url);
    },

    getFile(url, params, filterArray, sort, headers = {}) {
        const filter = constructFilter(filterArray);

        // Cancel previous request if it exists
        if (ongoingRequests[url]) {
            ongoingRequests[url].abort();
        }

        const controller = new AbortController();
        ongoingRequests[url] = controller;

        let options = {
            method: 'GET',
            url: url,
            responseType: 'blob',
            headers: {
                 ...headers,
                'Accept': 'text/csv',
                'Content-Type': 'text/csv'
            },
            signal: controller.signal // Include the signal in the options
        };

        return this.callAxios(options, url).then(function (response) {
            const link = document.createElement("a");

            const url = URL.createObjectURL(new Blob([response.data]));
            link.setAttribute("href", url);

            const fileName = getContentDispositionFilename(response.headers);
            link.setAttribute("download", fileName);

            link.click();
        });
    },

    async callAxios(options, url) {
        return new Promise((resolve, reject) => {
            AxiosInstance.request(options)
                .then(response => {
                    if (!options.signal.aborted) {
                        resolve(response);
                    } else {
                        reject('Request was aborted');
                    }
                })
                .catch(error => {
                    if (options.signal.aborted) {
                        reject('Request was aborted');
                    } else {
                        reject(error);
                    }
                });
        });
    }
}
const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}
export default ApiClient;
